import { cn } from "../../utils/cn";
import { faAngleDown, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import SearchOption from "./SearchOption";
import TopBar from "./TopBar";
import { NavLink, useLocation } from "react-router-dom";
import Loader from "../../common/Loader/Loader";

const SecondaryNavbar = ({ hideTopBar = false }) => {
  const location = useLocation();
  const [showSearch, setShowSearch] = useState(false);
  const [innerMobileMenu, setInnerMobileMenu] = useState(false);
  const [sticky, setSticky] = useState(false);
  const broker = import.meta.env.VITE_BROKER;
  const [data, setData] = useState();

  useEffect(() => {
    async function fetchData() {
      try {
        if (!broker) {
          console.error("Missing VITE_BROKER");

          return;
        }

        const module = await import(
          `../../data/${broker.toLowerCase()}/navbar.json`
        );

        const metadata = module?.menuData ?? module?.default?.menuData;

        if (!metadata) {
          console.error("Menudata not found in module", module);

          return;
        }

        setData(metadata);
      } catch (error) {
        console.error("Failed to load navbar.json", error);
      }
    }

    fetchData();
  }, [broker]);

  const handleStickyNavbar = useCallback(() => {
    setSticky(window.scrollY >= 20);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleStickyNavbar);

    return () => {
      window.removeEventListener("scroll", handleStickyNavbar);
    };
  }, [handleStickyNavbar]);

  if (!data) {
    return <Loader />;
  }

  return (
    <header>
      {!hideTopBar && <TopBar sticky={sticky} title={data.title} />}
      <div
        className={cn(
          "base:mb-16 fixed left-0 z-50 w-full bg-transparent transition-all duration-500 max-md:z-500",
          sticky ? "nav-sticky " : "",
          !hideTopBar ? "top-16" : "top-8"
        )}
      >
        <nav className="container relative flex items-center">
          <div className="nav-logo">
            <NavLink to="/">
              <img
                src={data.logoLight}
                alt="logo"
                className="dark:hidden"
                width={data.logoWidth}
                height={data.logoHeight}
              />
              <img
                src={data.logoDark}
                alt="logo dark version"
                className="hidden dark:inline-block"
                width={data.logoWidth}
                height={data.logoHeight}
              />
            </NavLink>
          </div>
          <ul className="nav-list hidden lg:ml-7 lg:flex xl:ml-15 [&>*:not(:last-child)]:me-1">
            {data.menuContent.map((menuItem) => (
              <li
                className={`${menuItem.megaMenu ? "group" : !menuItem.path ? "group relative" : ""}`}
                key={menuItem.id}
              >
                {menuItem.path ? (
                  <NavLink
                    to={menuItem.path}
                    className={cn(
                      "flex items-center rounded-large border border-transparent px-5 py-[5px] font-Inter text-base font-medium capitalize leading-8 text-paragraph transition-colors duration-500 hover:border-borderColor hover:bg-white hover:duration-500 dark:text-white dark:hover:border-borderColor/10 dark:hover:bg-dark-200 lg:px-4 xl:px-5",
                      menuItem.path && location === menuItem.path
                        ? "active"
                        : ""
                    )}
                  >
                    {menuItem.title}
                  </NavLink>
                ) : (
                  menuItem.megaMenu && (
                    <>
                      <NavLink
                        to="#"
                        className={cn(
                          "hover:border-borderColour dark:hover:border-borderColour/10 group flex items-center rounded-large border border-transparent px-5 py-[5px] font-Inter text-base font-medium leading-8 text-paragraph transition-colors duration-500 hover:bg-white hover:duration-500 dark:text-white dark:hover:bg-dark-200 lg:px-4 xl:px-5"
                        )}
                      >
                        {menuItem.title}
                        <FontAwesomeIcon
                          icon={faAngleDown}
                          className="ml-1 mt-1 text-paragraph duration-500 group-hover:rotate-180 dark:text-white"
                        />
                      </NavLink>
                      <div className="absolute left-0 top-12 z-10 grid w-full origin-top scale-y-0 items-center gap-15  rounded-medium bg-white p-2.5 text-gray-900 opacity-0 shadow-lg duration-500  group-hover:scale-y-100 group-hover:opacity-100 dark:bg-dark-200 dark:text-white md:grid-cols-12">
                        <ul className="col-span-8 columns-3 gap-10 px-15">
                          {menuItem.submenu.map((submenuItem) => (
                            <li
                              className="relative overflow-hidden py-2.5 text-base capitalize text-paragraph before:absolute before:bottom-0 before:left-0 before:h-[2px] before:w-full before:origin-right before:scale-x-0 before:bg-paragraph before:transition-transform  before:duration-500 before:content-[''] before:hover:origin-left before:hover:scale-x-100 dark:before:bg-white"
                              key={submenuItem.id}
                            >
                              <NavLink to={submenuItem.path} className="flex">
                                {submenuItem.title}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                        <div className="relative col-span-4 h-full">
                          <img
                            src={menuItem.imageLight}
                            width={350}
                            height={350}
                            alt="navbar"
                            className=" w-full! rounded-2xl dark:hidden"
                          />
                          <img
                            src={menuItem.imageDark}
                            width={350}
                            height={350}
                            alt="navbar"
                            className="hidden w-full! rounded-2xl  dark:block"
                          />
                        </div>
                      </div>
                    </>
                  )
                )}
              </li>
            ))}
          </ul>

          <ul className="ml-auto flex items-center  [&>*:not(:last-child)]:me-2.5">
            <li className="">
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="rounded-full bg-white p-2.5 dark:bg-dark-200 "
                id="open-btn"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.11278 0C14.1369 0 18.2245 4.08758 18.2245 9.11278C18.2245 11.2861 17.4592 13.5472 16.1845 14.8512L20 18.6667L18.6667 20L14.8512 16.1856C13.5667 17.4603 11.2861 18.2245 9.11278 18.2245C4.08758 18.2245 0 14.1369 0 9.11278C0 4.08758 4.08758 0 9.11278 0ZM9.11278 16.3395C13.0974 16.3395 16.3395 13.0974 16.3395 9.11278C16.3395 5.12818 13.0974 1.88608 9.11278 1.88608C5.12709 1.88608 1.88499 5.12818 1.88499 9.11278C1.88499 13.0974 5.12709 16.3395 9.11278 16.3395Z"
                    fill=""
                    className="fill-paragraph dark:fill-white"
                  />
                </svg>
              </button>
            </li>
            <li className="max-lg:hidden">
              <div className="group relative">
                <button className="btn btm-sm cursor-pointer flex items-center rounded-large border border-transparent px-5 py-[5px] text-computeButtonText font-Inter font-medium capitalize leading-8 text-computeButtonText transition-colors duration-500 hover:border-borderColor hover:bg-white hover:duration-500 dark:text-white dark:hover:border-borderColor/10 dark:hover:bg-dark-200 lg:px-4 xl:px-5">
                  Asigurare
                  <FontAwesomeIcon
                    icon={faAngleDown}
                    className="ml-1 text-computeButtonText dark:text-white duration-500 group-hover:rotate-180 dark:text-white dark:group-hover:text-black"
                  />
                </button>

                <ul className="absolute drop-shadow-xl right-0 top-12 z-10 min-w-[250px] origin-top scale-y-0 rounded-md bg-white p-5 opacity-0 duration-500 group-hover:scale-y-100 group-hover:opacity-100 dark:bg-dark-200 [&>*:not(:first-child)]:mt-2.5 [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-dashed [&>*:not(:last-child)]:border-borderColor dark:[&>*:not(:last-child)]:border-borderColor-dark">
                  <li className="relative overflow-hidden pb-2.5 text-base capitalize text-paragraph duration-500 before:absolute before:bottom-0 before:left-0 before:h-[2px] before:w-full before:origin-right before:scale-x-0 before:bg-paragraph before:transition-transform before:duration-500 before:content-[''] before:hover:origin-left before:hover:scale-x-100 dark:before:bg-white">
                    <NavLink to="/rca" className="flex">
                      RCA
                    </NavLink>
                  </li>

                  <li className="relative overflow-hidden pb-2.5 text-base capitalize text-paragraph duration-500 before:absolute before:bottom-0 before:left-0 before:h-[2px] before:w-full before:origin-right before:scale-x-0 before:bg-paragraph before:transition-transform before:duration-500 before:content-[''] before:hover:origin-left before:hover:scale-x-100 dark:before:bg-white">
                    <NavLink to="/pad" className="flex">
                      PAD
                    </NavLink>
                  </li>

                  <li className="relative overflow-hidden pb-2.5 text-base capitalize text-paragraph duration-500 before:absolute before:bottom-0 before:left-0 before:h-[2px] before:w-full before:origin-right before:scale-x-0 before:bg-paragraph before:transition-transform before:duration-500 before:content-[''] before:hover:origin-left before:hover:scale-x-100 dark:before:bg-white">
                    <NavLink to="/travel" className="flex">
                      Călătorie
                    </NavLink>
                  </li>

                  <li className="relative overflow-hidden pb-2.5 text-base capitalize text-paragraph duration-500 before:absolute before:bottom-0 before:left-0 before:h-[2px] before:w-full before:origin-right before:scale-x-0 before:bg-paragraph before:transition-transform before:duration-500 before:content-[''] before:hover:origin-left before:hover:scale-x-100 dark:before:bg-white">
                    <NavLink to="/house" className="flex">
                      Locuință
                    </NavLink>
                  </li>
                </ul>
              </div>
            </li>
            <li className="max-lg:inline-block lg:hidden ">
              <button
                className="mobile-menu-button relative h-10 w-10 rounded-full bg-white outline-none dark:bg-dark-200"
                onClick={() => setInnerMobileMenu(!innerMobileMenu)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="14"
                  viewBox="0 0 22 14"
                  fill="none"
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                >
                  <path
                    d="M0 1C0 0.447715 0.447715 0 1 0H21C21.5523 0 22 0.447715 22 1C22 1.55228 21.5523 2 21 2H1C0.447716 2 0 1.55228 0 1Z"
                    fill=""
                    className="fill-paragraph dark:fill-white"
                  />
                  <path
                    d="M8 7C8 6.44772 8.44772 6 9 6H21C21.5523 6 22 6.44772 22 7C22 7.55228 21.5523 8 21 8H9C8.44772 8 8 7.55228 8 7Z"
                    fill=""
                    className="fill-paragraph dark:fill-white"
                  />
                  <path
                    d="M4 13C4 12.4477 4.44772 12 5 12H21C21.5523 12 22 12.4477 22 13C22 13.5523 21.5523 14 21 14H5C4.44772 14 4 13.5523 4 13Z"
                    fill=""
                    className="fill-paragraph dark:fill-white"
                  />
                </svg>
              </button>
            </li>
          </ul>

          <div
            className={`mobile-menu max-lg:overflow-y-auto ${innerMobileMenu ? "open" : ""}`}
          >
            <button
              className=" navbar-toggle-close absolute right-6 top-5 h-10 w-10 rounded-full bg-white outline-none dark:bg-dark-200"
              onClick={() => setInnerMobileMenu(!innerMobileMenu)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <ul className="nav-list flex w-full max-w-[500px] flex-col gap-5 landscape:h-full">
              {data.menuContent.map((menuItem) => {
                if (!menuItem.megaMenu && menuItem.submenu) {
                  return menuItem.submenu.map((submenuItem) => (
                    <li
                      className={cn(
                        submenuItem.path ? "relative" : "group relative"
                      )}
                      key={submenuItem.id}
                    >
                      <NavLink
                        to={submenuItem.path}
                        className={cn(
                          "flex items-center rounded-large border border-transparent px-5 py-[5px] font-Inter text-base font-medium leading-8 text-paragraph transition-colors duration-500 hover:border-borderColor hover:bg-white hover:duration-500 dark:text-white dark:hover:border-borderColor/10 dark:hover:bg-dark-200 lg:px-4 xl:px-5",
                          submenuItem.path && location === submenuItem.path
                            ? "active"
                            : ""
                        )}
                        onClick={() => setInnerMobileMenu(!innerMobileMenu)}
                      >
                        {submenuItem.title}
                      </NavLink>
                    </li>
                  ));
                }

                return (
                  <li
                    className={`${menuItem.path ? "" : "group relative"}`}
                    key={menuItem.id}
                  >
                    {menuItem.path ? (
                      <NavLink
                        to={menuItem.path}
                        className={cn(
                          "flex items-center rounded-large border border-transparent px-5 py-[5px] font-Inter text-base font-medium leading-8 text-paragraph transition-colors duration-500 hover:border-borderColor hover:bg-white hover:duration-500 dark:text-white dark:hover:border-borderColor/10 dark:hover:bg-dark-200 lg:px-4 xl:px-5",
                          menuItem.path && location === menuItem.path
                            ? "active"
                            : ""
                        )}
                        onClick={() => setInnerMobileMenu(!innerMobileMenu)}
                      >
                        {menuItem.title}
                      </NavLink>
                    ) : (
                      menuItem.megaMenu && (
                        <>
                          <NavLink
                            to="#"
                            className="hover:border-borderColour dark:hover:border-borderColour/10 group flex items-center rounded-large border border-transparent px-5 py-[5px] font-Inter text-base font-medium leading-8 text-paragraph transition-colors duration-500 hover:bg-white hover:duration-500 dark:text-white dark:hover:bg-dark-200 lg:px-4 xl:px-5"
                          >
                            {menuItem.title}
                            <FontAwesomeIcon
                              icon={faAngleDown}
                              className="ml-auto mt-1 text-paragraph duration-500 group-hover:rotate-180 dark:text-white"
                            />
                          </NavLink>
                          <div className="absolute left-0 top-12 z-10 w-full origin-top scale-y-0  items-center rounded-medium bg-white p-5 text-gray-900 opacity-0 shadow-lg  duration-500 group-hover:scale-y-100 group-hover:opacity-100 dark:bg-dark-200 dark:text-white">
                            <ul className="mb-15 columns-2 gap-10">
                              {menuItem.submenu.map((submenuItem) => (
                                <li
                                  className="relative overflow-hidden py-2.5 text-base capitalize text-paragraph before:absolute before:bottom-0 before:left-0 before:h-[2px] before:w-full before:origin-right before:scale-x-0 before:bg-paragraph before:transition-transform  before:duration-500 before:content-[''] before:hover:origin-left before:hover:scale-x-100 dark:before:bg-white"
                                  key={submenuItem.id}
                                >
                                  <NavLink
                                    to={submenuItem.path}
                                    className="flex"
                                    onClick={() =>
                                      setInnerMobileMenu(!innerMobileMenu)
                                    }
                                  >
                                    {submenuItem.title}
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                            <div className="max-w-full">
                              <img
                                src={menuItem.imageLight}
                                width={350}
                                height={350}
                                alt="navbar"
                                className=" w-full! rounded-2xl dark:hidden"
                              />
                              <img
                                src={menuItem.imageDark}
                                width={350}
                                height={350}
                                alt="navbar"
                                className="hidden w-full! rounded-2xl  dark:block"
                              />
                            </div>
                          </div>
                        </>
                      )
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>
      </div>
      {showSearch &&
        createPortal(
          <SearchOption onClose={() => setShowSearch(false)} />,
          document.body
        )}
    </header>
  );
};

export default SecondaryNavbar;
